import { Container, Graphics, Text } from "pixi.js"

import { Obligation } from "./ember_obligations"
import { TextRenderer } from "../text/text_renderer"
import { controlHitRect, getUiMetrics, ResponsiveHitRect } from "./ui_metrics"
import { UI_THEMES } from "./ui_tokens"

/**
 * The camp's standing tasks: one row per live obligation, top-left, for as long as it is true.
 *
 * The counterpart to the `important` strip rather than a replacement for it. The strip says what
 * CHANGED and then leaves; this says what is still TRUE, so a player who was placing walls when the
 * ritual was revealed can still find out that it is running, and a player who was told once a minute
 * ago does not have to have remembered.
 *
 * TWO WAYS OUT, which is the whole reason this can be allowed to persist at all. A permanent HUD
 * panel that the player cannot get rid of is just more of the clutter it was built to cut:
 *
 *   * Each row's x DISMISSES that row until what it says changes (see Obligation.signature). It is
 *     "I have read this", not "never again" -- the ritual lighting its third stone, or the survivor
 *     coming under attack, brings the row straight back. So a dismissal can never hide news.
 *   * The header COLLAPSES the stack to a single pill carrying the count, and that choice is
 *     remembered across sessions like the minimap's. It never collapses to nothing: a player who has
 *     hidden two tasks can still see that there are two, and in what tone.
 *
 * Drawn only on a change (`paintKey`), so a settled camp pays nothing per frame. There is no
 * animation here on purpose -- a standing fact that pulses is an alarm, and the alert tone plus the
 * strip's own alert tier are already doing that job.
 */

const THEME = UI_THEMES.ember
/**
 * OPAQUE, not a translucent HUD plate.
 *
 * Everything else that floats over the world here is see-through, and this is the one thing that
 * must not be: it sits in the top band that the `important` strip crosses, so a translucent panel
 * put the camp's standing tasks and a transient notice about something else in the same pixels and
 * neither could be read. It is also the one panel that is up for minutes at a time rather than
 * seconds, which is exactly the case where legibility beats seeing the grass behind it.
 */
const PANEL_FILL = 0x12100d
const PANEL_ALPHA = 1
const PANEL_ALPHA_HOVER = 1
/** Hover lifts the FILL rather than the alpha, which has nowhere left to go. */
const PANEL_FILL_HOVER = 0x1f1a14
const CORNER = 6
const PAD_X = 8
const PAD_Y = 6
const ROW_GAP = 4
/** The coloured spine down the left of a row: the only thing that carries tone at a glance. */
const SPINE_W = 2.5
const SPINE_GAP = 6
/** Width of the whole panel. Fixed, so a detail changing length cannot make the stack twitch. */
const WIDTH = 206
const TOUCH_WIDTH = 240

type Row = {
    obligation: Obligation
    container: Container
    plate: Graphics
    label: Text
    detail: Text
    close: Container
    hovered: boolean
}

export class EmberObligationsPanel extends Container {
    private header = new Container()
    private headerPlate = new Graphics()
    private headerText: Text
    private rowsLayer = new Container()
    private rows: Row[] = []
    private obligations: Obligation[] = []
    private collapsed = false
    /**
     * Folded by the owner for as long as a notice is on the `important` strip; see setYielded.
     *
     * Kept apart from `collapsed` so the two can never be confused: `collapsed` is the player's own
     * remembered choice, this is a transient courtesy, and letting the strip go must not re-open a
     * stack the player folded themself.
     */
    private yielded = false
    /**
     * The player opened it while it was yielding, so stop yielding until the strip is quiet again.
     *
     * Without this the owner simply re-folds it on the very next frame, and the header reads as a
     * dead control for as long as the notice is up.
     */
    private yieldOverride = false
    private headerHovered = false
    /** What the last paint was of, so an unchanged feed costs nothing. */
    private paintKey = ""

    constructor(
        private onDismiss: (obligation: Obligation) => void,
        private onToggleCollapsed: (collapsed: boolean) => void,
    ) {
        super()
        this.headerText = new TextRenderer().role("overline").color(THEME.text).render("") as Text
        this.header.addChild(this.headerPlate, this.headerText)
        this.header.interactive = true
        this.header.cursor = "pointer"
        this.header.on("mouseover", () => {
            this.headerHovered = true
            this.repaint()
        })
        this.header.on("mouseout", () => {
            this.headerHovered = false
            this.repaint()
        })
        // A press OPENS it whatever is holding it shut, and closes it when it is open. Folding has
        // two causes (the player's own choice and making way for a notice) and the player should
        // never have to press twice to undo both, nor press once and see nothing change.
        const toggle = () => {
            if (this.folded()) {
                if (this.yielded) {
                    this.yieldOverride = true
                    this.yielded = false
                }
                if (this.collapsed) {
                    this.collapsed = false
                    this.onToggleCollapsed(false)
                }
            } else {
                this.collapsed = true
                this.onToggleCollapsed(true)
            }
            this.repaint()
        }
        this.header.on("click", toggle)
        this.header.on("tap", toggle)
        this.addChild(this.rowsLayer, this.header)
        this.visible = false
    }

    /** Idempotent: the owner re-asserts the remembered choice from its layout pass. */
    setCollapsed = (collapsed: boolean) => {
        if (this.collapsed === collapsed) return
        this.collapsed = collapsed
        this.repaint()
    }

    isCollapsed = (): boolean => this.collapsed

    /**
     * Fold the rows away while the strip is speaking, and let them back once it has finished.
     *
     * The strip's band crosses the row the open rows hang into, so the two TAKE TURNS rather than
     * sharing the pixels: the notice says what changed, then the standing list unfolds behind it.
     * Drawing one over the other was legible, but it said two things at once, in one band, about
     * different events -- which is the noise this whole feature exists to cut.
     *
     * It folds to the PILL rather than disappearing: the header sits above the band, so the count
     * and the tone stay on screen and nothing is actually withheld while the strip has the floor.
     * A fold also reads as the panel making room, where a vanish reads as the task being over.
     */
    setYielded = (yielded: boolean) => {
        // The strip has gone quiet, so an override has nothing left to override.
        if (!yielded) this.yieldOverride = false
        const next = yielded && !this.yieldOverride
        if (this.yielded === next) return
        this.yielded = next
        this.repaint()
    }

    isYielded = (): boolean => this.yielded

    /** Folded for either reason: the player's own choice, or making way for a notice. */
    private folded = (): boolean => this.collapsed || this.yielded

    /** Whether the camp owes the player anything the player has not waved away. */
    hasTasks = (): boolean => this.obligations.length > 0

    /**
     * Just the header bar's height.
     *
     * The owner centres the header on the HP bar's line (see placeEmberObligations) -- the rows hang
     * BELOW it like any dropdown, so it is the header, not the whole stack, that has to line up.
     */
    getHeaderHeight = (): number => this.headerHeight()

    update = (obligations: Obligation[]) => {
        this.obligations = obligations
        this.visible = obligations.length > 0
        if (!this.visible) {
            // Keep the rows around: the next camp event is likely the same two keys back again.
            this.paintKey = ""
            return
        }
        this.repaint()
    }

    private width_ = () => (getUiMetrics().touch ? TOUCH_WIDTH : WIDTH)

    getWidth = (): number => this.width_()

    getHeight = (): number => {
        const header = this.headerHeight()
        if (this.folded() || this.obligations.length < 1) return header
        return this.rows.reduce((total, row) => total + this.rowHeight(row) + ROW_GAP, header + ROW_GAP) - ROW_GAP
    }

    private headerHeight = () => Math.round(this.headerText.height) + PAD_Y * 2
    private rowHeight = (row: Row) => Math.round(row.label.height + row.detail.height) + PAD_Y * 2 + 1

    private repaint = () => {
        const key = [
            this.collapsed ? "c" : "o",
            this.yielded ? "y" : "-",
            this.headerHovered ? "h" : "-",
            getUiMetrics().touch ? "t" : "d",
            ...this.obligations.map(o => `${o.key}|${o.tone}|${o.label}|${o.detail}`),
            ...this.rows.map(r => (r.hovered ? "1" : "0")),
        ].join("~")
        if (key === this.paintKey) return
        this.paintKey = key
        this.syncRows()
        this.draw()
    }

    /** One row container per obligation, reused across repaints and keyed by obligation. */
    private syncRows = () => {
        const keep = new Set(this.obligations.map(o => o.key))
        for (const row of this.rows.filter(r => !keep.has(r.obligation.key))) {
            this.rowsLayer.removeChild(row.container)
            row.container.destroy({ children: true })
        }
        this.rows = this.rows.filter(r => keep.has(r.obligation.key))
        for (const obligation of this.obligations) {
            const existing = this.rows.find(r => r.obligation.key === obligation.key)
            if (existing) {
                existing.obligation = obligation
                continue
            }
            this.rows.push(this.buildRow(obligation))
        }
        // Feed order, so the stack does not reshuffle as tones change.
        this.rows.sort(
            (a, b) =>
                this.obligations.findIndex(o => o.key === a.obligation.key) -
                this.obligations.findIndex(o => o.key === b.obligation.key),
        )
        this.rowsLayer.removeChildren()
        this.rows.forEach(row => this.rowsLayer.addChild(row.container))
    }

    private buildRow = (obligation: Obligation): Row => {
        const container = new Container()
        const plate = new Graphics()
        const label = new TextRenderer().role("compactBody").color(THEME.text).render("") as Text
        const detail = new TextRenderer().role("compactCaption").color(THEME.text).render("") as Text
        const close = this.buildClose()
        container.addChild(plate, label, detail, close)
        const row: Row = { obligation, container, plate, label, detail, close, hovered: false }
        container.interactive = true
        container.on("mouseover", () => {
            row.hovered = true
            this.repaint()
        })
        container.on("mouseout", () => {
            row.hovered = false
            this.repaint()
        })
        close.on("click", () => this.onDismiss(row.obligation))
        close.on("tap", () => this.onDismiss(row.obligation))
        return row
    }

    /**
     * The dismiss control: a small ring with a cross, its hit area grown to a thumb on touch.
     *
     * Drawn once rather than per repaint -- it never changes -- and parented to its row so it
     * travels with it.
     */
    private buildClose = (): Container => {
        const close = new Container()
        const mark = new Graphics()
        const r = 6
        mark.lineStyle(1, THEME.border, 0.9).drawCircle(r, r, r)
        mark.lineStyle(1.4, THEME.text, 0.75)
            .moveTo(r - 2.6, r - 2.6).lineTo(r + 2.6, r + 2.6)
            .moveTo(r + 2.6, r - 2.6).lineTo(r - 2.6, r + 2.6)
        close.addChild(mark)
        close.interactive = true
        close.cursor = "pointer"
        close.hitArea = new ResponsiveHitRect(() => controlHitRect(close, r * 2, r * 2, 8))
        return close
    }

    private draw = () => {
        const w = this.width_()
        const headerH = this.headerHeight()
        const alertCount = this.obligations.filter(o => o.tone === "alert").length
        const hot = alertCount > 0

        this.headerText.text = this.folded()
            ? `▸  ${this.obligations.length} ${this.obligations.length === 1 ? "TASK" : "TASKS"}`
            : "▾  CAMP TASKS"
        this.headerText.tint = hot ? THEME.accent : THEME.text
        this.headerText.alpha = this.headerHovered ? 1 : 0.85
        // Folded, the header hugs its own text: a pill, not a full-width bar with nothing under it.
        const headerW = this.folded() ? Math.round(this.headerText.width) + PAD_X * 2 : w
        this.headerPlate.clear()
        this.headerPlate
            .beginFill(this.headerHovered ? PANEL_FILL_HOVER : PANEL_FILL, this.headerHovered ? PANEL_ALPHA_HOVER : PANEL_ALPHA)
            .lineStyle(1, hot ? THEME.accent : THEME.border, this.headerHovered ? 0.9 : 0.55)
            .drawRoundedRect(0, 0, headerW, headerH, CORNER)
            .endFill()
        this.headerText.x = PAD_X
        this.headerText.y = PAD_Y
        this.header.hitArea = new ResponsiveHitRect(() => controlHitRect(this.header, headerW, headerH, 6))

        this.rowsLayer.visible = !this.folded()
        if (this.folded()) return

        let y = headerH + ROW_GAP
        for (const row of this.rows) {
            const { obligation } = row
            row.label.text = `${obligation.icon}  ${obligation.label}`
            row.detail.text = obligation.detail
            const tone = obligation.tone === "alert" ? THEME.accent : THEME.border
            row.detail.tint = obligation.tone === "alert" ? THEME.accent : THEME.text
            row.detail.alpha = obligation.tone === "alert" ? 0.95 : 0.6
            const h = this.rowHeight(row)
            row.plate.clear()
            row.plate
                .beginFill(row.hovered ? PANEL_FILL_HOVER : PANEL_FILL, row.hovered ? PANEL_ALPHA_HOVER : PANEL_ALPHA)
                .lineStyle(1, tone, row.hovered ? 0.8 : 0.45)
                .drawRoundedRect(0, 0, w, h, CORNER)
                .endFill()
            // The spine, inside the rim: tone without tinting the text the player has to read.
            row.plate.lineStyle(0).beginFill(tone, obligation.tone === "alert" ? 0.95 : 0.5)
                .drawRoundedRect(2, 4, SPINE_W, h - 8, SPINE_W / 2).endFill()
            const textX = 2 + SPINE_W + SPINE_GAP
            row.label.x = textX
            row.label.y = PAD_Y
            row.detail.x = textX
            row.detail.y = PAD_Y + Math.round(row.label.height) + 1
            row.close.x = w - PAD_X - 12
            row.close.y = Math.round((h - 12) / 2)
            row.container.hitArea = new ResponsiveHitRect(() => controlHitRect(row.container, w, h, 0))
            row.container.y = y
            y += h + ROW_GAP
        }
    }
}
